import { ClickAwayListener } from "@mui/material";
import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { isClient } from "../../globals";
import { findFirstComponentOfType } from "../internal/TextExtractor";
import { useToggleSwitchController } from "../../state/toggle-switch-controller.context";

interface ToggleSwitchProps {
    name: string,
    isDefault: boolean,
    defaultHidden?: boolean,
    clickAwayBreakpoint: number,
    hiddenBelowBreakpoint: number,
    children: React.ReactNode,
}

/***
 * A component that renders a toggle switch.
 * This can be used for collapsing and expanding content, or for hiding/showing drawers etc.
 * The controllerElement is the element that is used to control the expansion.
 * The controlledElement is the element that is expanded or collapsed.
 * The clickAwayBreakpoint is the screen width below which a click away from the controlled element will close it.
 * The hiddenBelowBreakpoint is the screen width below which the controlled element is hidden when collapsed. By
 * using the hidden=until-found attribute, the browser will open the expansion when the user gets a search match.
 */
export function ToggleSwitch({ name, isDefault, defaultHidden, clickAwayBreakpoint, hiddenBelowBreakpoint, children }: ToggleSwitchProps): JSX.Element {
    const controller = useToggleSwitchController();
    const isControlled = !!controller;
    const controllerRegister = controller?.register;
    const controllerUnregister = controller?.unregister;
    const controllerIsRegistered = controller?.isRegistered;
    const controllerOpen = controller?.open;
    const controllerToggle = controller?.toggle;
    const controllerClose = controller?.close;
    const controllerPolicy = controller?.policy;
    const initialOn = defaultHidden !== undefined ? !defaultHidden : (isDefault || false);
    const initialOnRef = useRef<boolean>(initialOn);
    const buttonRef = useRef<HTMLDivElement>(null);
    const [standaloneOn, setStandaloneOn] = useState<boolean>(initialOnRef.current);
    const [controlledNode, setControlledNode] = useState<Node | null>(null);
    const on = isControlled
        ? (
            (controller?.isOpen(name) ?? false)
            || (!(controllerIsRegistered?.(name) ?? false) && initialOnRef.current)
        )
        : standaloneOn;

    // Force reload when the window is resized
    const [, setDimensions] = useState(isClient ? {
        height: window.innerHeight,
        width: window.innerWidth
    } : { height: 0, width: 0 });

    useEffect(() => {
        if (isClient) {
            function handleResize() {
                setDimensions({
                    height: window.innerHeight,
                    width: window.innerWidth
                });
            }
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, []);

    // Event handler for click on the controller
    const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
        if (isControlled) {
            // Do not call manageHidden here: the controller may ignore the click (e.g. exactlyOne sole active).
            // If we optimistically hid the node and toggle bails out (same Set reference), React would not re-run
            // the sync effect — controlled visibility must follow `on` via useEffect below.
            controllerToggle?.(name);
            // exactlyOne does not dismiss on click-away.
            // zeroOrOne already closes every other switch in toggle, so this click must not reach ClickAwayListener.
            // multi must reach sibling listeners. This switch ignores its own button in handleClickAway.
            if (controllerPolicy !== "multi") {
                event.stopPropagation();
            }
        } else {
            manageHidden(!on, controlledNode as HTMLDivElement);
            setStandaloneOn(!on);
            event.stopPropagation();
        }
    }

    // Sets hidden=until-found to enable browser to open expansion when the user gets
    // a search match inside the collapsed content
    const manageHidden = useCallback((isOn: boolean, node: HTMLDivElement) => {
        if (isOn || window.innerWidth >= hiddenBelowBreakpoint) {
            node?.removeAttribute("hidden");
        }
        else {
            node?.setAttribute("hidden", "until-found");
        }
    }, [hiddenBelowBreakpoint]);

    // Click-away handler for closing the expansion, typically used for narrow screens
    const handleClickAway = (evt: MouseEvent | TouchEvent) => {
        if (isControlled) {
            // Close only. Do not toggle, open, or touch hidden state here — visibility follows `on`.
            if (controllerPolicy !== "zeroOrOne" && controllerPolicy !== "multi") {
                return;
            }
            // The controller button is outside the listener child. Under multi the click still
            // bubbles so siblings can dismiss; this switch must not close itself on that click.
            const target = evt.target;
            if (buttonRef.current && target instanceof Node && buttonRef.current.contains(target)) {
                return;
            }
            if (on && clickAwayBreakpoint && window.innerWidth < clickAwayBreakpoint) {
                controllerClose?.(name);
            }
            return;
        }
        if (on && clickAwayBreakpoint && window.innerWidth < clickAwayBreakpoint) {
            manageHidden(false, controlledNode as HTMLDivElement);
            setStandaloneOn(false);
            // Make sure the click away doesn't trigger the click on the button
            evt.stopPropagation();
            evt.preventDefault();
        }
    }

    // Add an effect that sets up an event listener for beforematch
    // This is used to open an expansion when the user gets a search match inside the collapsed content
    // We have to use native since the event is not exposed in React
    useEffect(() => {
        if (isClient) {
            function handleBeforeMatch(_evt: Event) {
                if (isControlled) {
                    controllerOpen?.(name);
                } else {
                    setStandaloneOn(true);
                }
            }
            if (controlledNode && hiddenBelowBreakpoint) {
                controlledNode.addEventListener("beforematch", handleBeforeMatch);
                return () => controlledNode.removeEventListener("beforematch", handleBeforeMatch);
            }
        }
    }, [controlledNode, controllerOpen, hiddenBelowBreakpoint, isControlled, name]);

    const fixNativeNode = (node: HTMLDivElement | null) => {
        if (node && hiddenBelowBreakpoint) {
            setControlledNode(node);
            manageHidden(on, node);
        }
    }

    useEffect(() => {
        if (!controllerRegister || !controllerUnregister) {
            return;
        }
        controllerRegister(name, initialOnRef.current);
        return () => controllerUnregister(name);
    }, [controllerRegister, controllerUnregister, name]);

    useEffect(() => {
        if (!controlledNode || !hiddenBelowBreakpoint) {
            return;
        }
        manageHidden(on, controlledNode as HTMLDivElement);
    }, [controlledNode, hiddenBelowBreakpoint, on, manageHidden]);

    const contentId = `_toggle-content-${name}`;
    // Get the controller child
    const controllerChild = useMemo(() => findFirstComponentOfType(children, "controller"), [children]);

    // Get the controlled child
    const controlledChild = useMemo(() => findFirstComponentOfType(children, "controlled"), [children]);
    // Optional closed-state content that replaces controlled content visually while collapsed
    const fallbackChild = useMemo(() => findFirstComponentOfType(children, "fallback"), [children]);
    return <>
        <div
            ref={buttonRef}
            className="_toggle-button"
            onClick={handleClick}
            aria-expanded={on}
            aria-controls={contentId}
            aria-label={name}
        >
            {controllerChild?.props.children}
        </div>
        {!on && fallbackChild && (
            <div className="_toggle-fallback">
                {fallbackChild.props.children}
            </div>
        )}
        <ClickAwayListener onClickAway={handleClickAway}>
            <div
                id={contentId}
                ref={(node) => fixNativeNode(node)} className={`_toggle-content ${on ? "_toggle-on" : "_toggle-off"} `}
            >
                {controlledChild?.props.children}
            </div>
        </ClickAwayListener>
    </>
}
